import { type ReactNode, useCallback, useMemo } from 'react';
import type { GraphQLFormattedError } from 'graphql';
import { noop } from 'lodash';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import { startEvent } from '@frameio/telemetry';
import { CoreAssetsEventNames } from '@frameio/telemetry/events/analytics/core-assets';
import { BULK_OPERATION_THRESHOLD } from '../../../bulk-operations/constants';
import type { AssetParentFragment } from '../../../shared/gql/generated/AssetParent';
import { useAssetOperationCheck } from '../../../shared/hooks/useAssetOperationCheck';
import { CopyCommentsOptions } from '../../constants';
import { checkCanPerformBulkAction } from '../../utils';
import {
  type AssetOperationArgs,
  useAssetWarningFlow,
} from '../useAssetWarningFlow';
import { useBulkActionLimitToast } from '../useBulkActionLimitToast';
import TEXT from './generated/TEXT/useMoveAssets';
import { useBulkMoveAssets } from './useBulkMoveAssets';
import { useSyncMoveAssets } from './useSyncMoveAssets';

type MoveAssetsArgs = AssetOperationArgs & {
  /** the array of asset ids to move */
  assetIds: string[];
  /** the destination folder asset (requires id, name and projectId) */
  destination: AssetParentFragment;
  /** whether to force the move, skipping any checks */
  force?: boolean;
  /** whether private comments should move to new workspace */
  movePrivateCommentsToNewWorkspace?: boolean;
  /** whether the comments are changing workspaces */
  changingWorkspaces?: boolean;
  /** callback fired on successful move */
  onSuccess?: (destination: AssetParentFragment) => void;
  /** the id of the source folder asset */
  sourceId: string;
};

export type MoveAssetsReturn = {
  moveAssets: (args: MoveAssetsArgs) => void;
  moveRestrictedFolderDialog: ReactNode;
  moveCrossProjectDialog: ReactNode;
};

/**
 * The useMoveAssets hook can be used to move a set of assets given an array of
 * asset ids. It performs any necessary cache cleanup on all assets that were moved
 * successfully.
 *
 * @returns a function to call when ready to move the assets
 */
export function useMoveAssets(): MoveAssetsReturn {
  const moveAssets = useSyncMoveAssets();
  const bulkMoveAssets = useBulkMoveAssets();
  const addBulkActionLimitToast = useBulkActionLimitToast();
  const track = useTrackUnsafe();
  const { checkMoveCopyPreconditions } = useAssetOperationCheck();

  const executeDirectMove = useCallback(
    async ({
      assetIds,
      destination,
      movePrivateCommentsToNewWorkspace = false,
      changingWorkspaces = false,
      isMoveToParent = false,
      onSuccess = noop,
      sourceId,
    }: MoveAssetsArgs) => {
      const assetCount = assetIds.length;
      const isBulkAssetOperation = assetCount >= BULK_OPERATION_THRESHOLD;
      let commentOption = CopyCommentsOptions.ALL;
      if (changingWorkspaces && !movePrivateCommentsToNewWorkspace) {
        commentOption = CopyCommentsOptions.PUBLIC;
      }

      track(
        'assets-moved',
        {
          assets_count: assetCount,
          is_bulk_operation: isBulkAssetOperation,
          comment_option: commentOption,
          is_move_to_parent: isMoveToParent,
        },
        { logToDatadog: true }
      );

      if (
        !checkCanPerformBulkAction({
          assetCount: assetIds.length,
          action: 'move',
        })
      ) {
        addBulkActionLimitToast('move');
        return;
      }

      if (isBulkAssetOperation) {
        await bulkMoveAssets({
          assetIds,
          destination,
          movePrivateCommentsToNewWorkspace,
        });
      } else {
        const moveEvent = startEvent(CoreAssetsEventNames.MoveAssets, {
          asset_count: assetCount,
          assets_ids: assetIds,
          source_id: sourceId,
          destination_id: destination.id,
        });

        const handleError = (error?: readonly GraphQLFormattedError[]) =>
          moveEvent.failed({ error });

        await moveAssets({
          assetIds,
          destination,
          sourceId,
          movePrivateCommentsToNewWorkspace,
          onError: handleError,
          onSuccess: (dest) => {
            moveEvent.completed();
            onSuccess(dest);
          },
        });
      }
    },
    [addBulkActionLimitToast, bulkMoveAssets, moveAssets, track]
  );

  const { warningState, startWarningFlow, createWarningDialog } =
    useAssetWarningFlow<MoveAssetsArgs>({
      executeOperation: executeDirectMove,
      cancelText: TEXT.cancel,
    });

  const moveAssetsCallback = useCallback(
    async (args: MoveAssetsArgs) => {
      const { assetIds, force = false, destination } = args;

      if (force) {
        executeDirectMove(args);
      } else {
        const checkResult = await checkMoveCopyPreconditions({
          assetIds,
          destination,
        });

        if (checkResult.shouldShowWarning) {
          startWarningFlow({
            ...args,
            triggeredByRestricted: checkResult.isAnyRestricted,
            triggeredByCrossProject: checkResult.isCrossProject,
          });
        } else {
          executeDirectMove(args);
        }
      }
    },
    [executeDirectMove, checkMoveCopyPreconditions, startWarningFlow]
  );

  const moveRestrictedFolderTitle = TEXT.title;
  const moveRestrictedFolderDetails = TEXT.details;
  const moveRestrictedFolderConfirm = TEXT.move;
  const moveRestrictedFolderDialog = useMemo(() => {
    return createWarningDialog({
      isShowing: warningState === 'restricted',
      title: moveRestrictedFolderTitle,
      details: moveRestrictedFolderDetails,
      confirmButtonText: moveRestrictedFolderConfirm,
    });
  }, [
    createWarningDialog,
    warningState,
    moveRestrictedFolderTitle,
    moveRestrictedFolderDetails,
    moveRestrictedFolderConfirm,
  ]);

  const moveCrossProjectWarningConfirm = TEXT.moveAnyway;
  const moveCrossProjectWarningTitle = TEXT.metadataWarningTitle;
  const moveCrossProjectWarningDescription = TEXT.metadataWarningDescription;
  const moveCrossProjectDialog = useMemo(() => {
    return createWarningDialog({
      isShowing: warningState === 'cross-project',
      title: moveCrossProjectWarningTitle,
      details: moveCrossProjectWarningDescription,
      confirmButtonText: moveCrossProjectWarningConfirm,
    });
  }, [
    createWarningDialog,
    warningState,
    moveCrossProjectWarningConfirm,
    moveCrossProjectWarningTitle,
    moveCrossProjectWarningDescription,
  ]);

  return {
    moveAssets: moveAssetsCallback,
    moveRestrictedFolderDialog,
    moveCrossProjectDialog,
  };
}

TEXT.DEFINE({
  cancel: 'Cancel',
  details: 'All folder members will lose access. Only you will retain access.',
  move: 'Move',
  title: "You're about to move a restricted folder",
  metadataWarningTitle: 'Custom fields will be lost',
  metadataWarningDescription:
    'Any custom fields or field values will be lost when assets are moved to a new project.',
  moveAnyway: 'Move Anyway',
});
